import * as Sentry from "@sentry/react"
import { getValidTokenForRequest } from "@/lib/token-validation"
import type {
  AttachmentItem,
  BankAccountCreate,
  BankAccountType,
  ReservationOfRightsStatus,
} from "./api"
import { SignatureServiceMap } from "./api"
import { useAppStore } from "./app-store"
import { ApiRequestError, ExpiredTokenError } from "./errors"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

export type SeveranceDocumentStatus = "in_process" | "signed" | "rejected"

type SignatureService =
  (typeof SignatureServiceMap)[keyof typeof SignatureServiceMap]

export type SeveranceDocument = {
  id: number
  name: string
  service: SignatureService
  status: SeveranceDocumentStatus
  file_url: string
  attachments: AttachmentItem[]
  created_at: string
  updated_at: string
}

export type SeveranceStartResponse = {
  success: boolean
  next_step: string
}

export type SeveranceRightsStatus = ReservationOfRightsStatus

export type SeveranceBankAccountResponse = {
  id: number
  holder_id: string
  holder_name: string
  number: string
  type: BankAccountType
  bank_id: number
  bank: {
    id: number
    name: string
    code: string
    disabled: boolean
  }
  confirmed_at: string | null
}

export type SeveranceBankAccountStatus = {
  bank_account: SeveranceBankAccountResponse | null
}

export type SeveranceCompleteResponse = {
  redirect_url: string
}

export type SeveranceSigneeStatus =
  | "in_process"
  | "signing"
  | "signed"
  | "failed"
  | "rejected"

export type SeveranceSignee = {
  id: number
  email: string
  status: SeveranceSigneeStatus
}

async function apiGet<TData>(path: string, token?: string): Promise<TData> {
  const headers = new Headers({
    "Content-Type": "application/json",
  })

  if (token) headers.set("Authorization", `Bearer ${token}`)

  return fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers,
  }).then(async (response) => {
    if (response.ok) {
      return response.json() as Promise<TData>
    }

    let body
    try {
      body = await response.json()
    } catch (jsonError) {
      Sentry.captureException(jsonError, {
        tags: {
          errorType: "non_json_error_response",
          httpMethod: "GET",
          apiModule: "severance",
        },
        extra: {
          url: `${API_BASE_URL}${path}`,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          responseType: response.type,
          originalError: jsonError,
        },
      })

      body = {
        type: "NetworkError",
        message:
          "Ha ocurrido un error de conexión. Por favor, inténtalo de nuevo.",
      }
    }
    throw new ApiRequestError(body, response)
  })
}

async function apiPost<TData>(
  path: string,
  json?: Record<string, unknown> | null,
  token?: string,
): Promise<TData> {
  const headers = new Headers({
    "Content-Type": "application/json",
  })

  if (token) headers.set("Authorization", `Bearer ${token}`)

  return fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    body: json ? JSON.stringify(json) : undefined,
    headers,
  }).then(async (response) => {
    if (response.ok) {
      if (response.status === 204) {
        return Promise.resolve(null) as Promise<TData>
      }
      return response.json() as Promise<TData>
    }

    let body
    try {
      body = await response.json()
    } catch (jsonError) {
      Sentry.captureException(jsonError, {
        tags: {
          errorType: "non_json_error_response",
          httpMethod: "POST",
          apiModule: "severance",
        },
        extra: {
          url: `${API_BASE_URL}${path}`,
          requestBody: json,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          responseType: response.type,
          originalError: jsonError,
        },
      })

      body = {
        type: "NetworkError",
        message:
          "Ha ocurrido un error de conexión. Por favor, inténtalo de nuevo.",
      }
    }
    throw new ApiRequestError(body, response)
  })
}

async function apiPut<TData>(
  path: string,
  json?: Record<string, unknown> | null,
  token?: string,
): Promise<TData> {
  const headers = new Headers({
    "Content-Type": "application/json",
  })

  if (token) headers.set("Authorization", `Bearer ${token}`)

  return fetch(`${API_BASE_URL}${path}`, {
    method: "PUT",
    body: json ? JSON.stringify(json) : undefined,
    headers,
  }).then(async (response) => {
    if (response.ok) {
      if (response.status === 204) {
        return Promise.resolve(null) as Promise<TData>
      }
      return response.json() as Promise<TData>
    }

    let body
    try {
      body = await response.json()
    } catch (jsonError) {
      Sentry.captureException(jsonError, {
        tags: {
          errorType: "non_json_error_response",
          httpMethod: "PUT",
          apiModule: "severance",
        },
        extra: {
          url: `${API_BASE_URL}${path}`,
          requestBody: json,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          responseType: response.type,
          originalError: jsonError,
        },
      })

      body = {
        type: "NetworkError",
        message:
          "Ha ocurrido un error de conexión. Por favor, inténtalo de nuevo.",
      }
    }
    throw new ApiRequestError(body, response)
  })
}

const getTokenForRequest = (token_id: string): string | undefined => {
  return getValidTokenForRequest(() => useAppStore.getState().tokens[token_id])
}

export const getSeveranceDocument = ({
  document_id,
  token_id,
}: {
  document_id: number
  token_id: string
}) =>
  apiGet<SeveranceDocument>(
    `/sign/severance/documents/${document_id}`,
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const startSeveranceProcess = ({ token_id }: { token_id: string }) =>
  apiPost<SeveranceStartResponse>(
    "/sign/severance/start",
    null,
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const getSeveranceRightsStatus = ({ token_id }: { token_id: string }) =>
  apiGet<SeveranceRightsStatus>(
    "/sign/severance/rights/status",
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const submitSeveranceRights = (data: {
  reservation_of_rights: boolean
  reservation_of_rights_content: string | null
  token_id: string
}) =>
  apiPost<SeveranceRightsStatus>(
    "/sign/severance/rights",
    {
      reservation_of_rights: data.reservation_of_rights,
      reservation_of_rights_content: data.reservation_of_rights_content,
    },
    getTokenForRequest(data.token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const getSeveranceBankAccount = ({ token_id }: { token_id: string }) =>
  apiGet<SeveranceBankAccountStatus>(
    "/sign/severance/bank-account",
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const updateSeveranceBankAccount = ({
  holder_id,
  holder_name,
  number,
  type,
  bank_id,
  token_id,
}: BankAccountCreate & {
  token_id: string
}) =>
  apiPut<SeveranceBankAccountStatus>(
    "/sign/severance/bank-account",
    { holder_id, holder_name, number, type, bank_id },
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const confirmSeveranceBankAccount = ({
  token_id,
}: {
  token_id: string
}) =>
  apiPost<SeveranceBankAccountStatus>(
    "/sign/severance/bank-account/confirm",
    null,
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const completeSeveranceProcess = ({ token_id }: { token_id: string }) =>
  apiPost<SeveranceCompleteResponse>(
    "/sign/severance/complete",
    null,
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const getSeveranceSignee = ({
  document_id,
  signee_id,
  token_id,
}: {
  document_id: number
  signee_id: number
  token_id: string
}) =>
  apiGet<SeveranceSignee>(
    `/sign/severance/documents/${document_id}/signees/${signee_id}`,
    getTokenForRequest(token_id),
  ).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })
